'use client'
import { SubmissionSteps } from './reportIndividualPage'
import { useEffect, useState, useRef } from 'react'
import { useFormContext } from './formContext'
import HCaptcha from '@hcaptcha/react-hcaptcha'
import { TitleAndInput } from './inputFields'
import axios, { AxiosError } from 'axios'
import { convertToFormData } from './utils'
import { server_name, spinner } from '../reportingTool/reportingUtils'

interface IProps {
  setCurrentStep: (Step: SubmissionSteps) => void
  markStepAsCompleted: (step: SubmissionSteps) => void
  currentStep: SubmissionSteps
  setIsSubmitted: (isSubmitted: boolean) => void
}

export const ContactInfo: React.FC<IProps> = ({
  setCurrentStep,
  markStepAsCompleted,
  currentStep,
  setIsSubmitted,
}: IProps) => {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const captchaRef = useRef<any>(null)

  const { data, handleFieldChange } = useFormContext()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasError, setErrorText] = useState<undefined | string>(undefined)

  const onClickBack = () => {
    setCurrentStep(currentStep - 1)
    const hasEmail = data.email.length > 1
    if (hasEmail) markStepAsCompleted(SubmissionSteps.Contact)
  }

  const onClickSubmit = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (data.email.length && !emailRegex.test(data.email.trim())) {
      setErrorText('Please enter a valid email')
      return
    }
    setErrorText(undefined)
    captchaRef?.current?.execute()
    setIsLoading(true)
  }

  const onVerify = async (token: string) => {
    try {
      setIsLoading(true)
      const formData = convertToFormData(data)
      formData.append('token', token)
      if (!server_name) throw new Error('Server name is not set')
      await axios.post(`${server_name}/submit-form`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      setIsSubmitted(true)
    } catch (error) {
      console.error('Error submitting form:', error)
      setIsLoading(false)
      const axiosError = error as AxiosError<{ message: string; details?: string }>
      const errorMessage =
        axiosError.response?.data?.message || axiosError.message || 'An unknown error occurred.'
      const errorStatus = axiosError.response?.status
      const errorDetails = axiosError.response?.data?.details || ''
      alert(`Error ${errorStatus}: ${errorMessage} ${errorDetails}`)
    }
  }

  const renderSpinner = (): JSX.Element | null => {
    if (!isLoading) return null
    return spinner
  }

  return (
    <div>
      <div className="text-[20px] mb-[10px] font-bold leading-[30px] md:text-[30px] md:leading-[45px]">
        Contact Information
      </div>
      <div className="text-[16px] font-medium leading-[21px] mb-[20px] md:text-[16px] md:leading-[24px]">
        If you wish to be contacted about the status of your submission, please enter your email
        address here
      </div>
      <div className="border-[1px] rounded-[8px] bg-gray-100 border-gray-600 p-4">
        <TitleAndInput
          errorText={hasError}
          title={'Email Address'}
          required={false}
          type={'email'}
          inputValue={data.email}
          name={'email'}
          placeholder={'Enter your email address'}
          handleFieldChange={handleFieldChange}
        />
      </div>
      <div className="opacity-0 absolute top-0 left-0 h-0 w-0 -z-10">
        <div>Phone number</div>
        <input
          type="text"
          id="phone"
          value={data.phone}
          onChange={handleFieldChange}
          placeholder="Enter Your phone number"
          name="phone"
        />
      </div>
      <HCaptcha
        size="invisible"
        ref={captchaRef}
        sitekey={'366080e6-99da-4baa-a5a4-7b6e89f09bee'}
        onVerify={onVerify}
        onClose={() => setIsLoading(false)}
        onExpire={() => setIsLoading(false)}
        onChalExpired={() => setIsLoading(false)}
      />
      <div className={'flex flex-row justify-center align-center my-2'}>
        <button
          className={`!text-white bg-black w-[100px] px-[5px] py-[3px] m-[5px] border-[1px] rounded-[8px]`}
          onClick={() => onClickBack()}
        >
          Back
        </button>
        <button
          className={`!text-black flex flex-row items-center justify-center bg-feYellow w-[100px] px-[5px] py-[3px] m-[5px] border-[1px] rounded-[8px]`}
          onClick={() => onClickSubmit()}
        >
          <div>Submit</div>
          {renderSpinner()}
        </button>
      </div>
    </div>
  )
}
